import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import AddNews from '../modal/AddNews';
import EditNews from '../modal/EditNews';

interface NewsItem {
  id: number;
  title: string;
  body: string;
  emoji: string;
  emojiColor: string;
  isPinned: boolean;
  authorId: number;
  createdAt: string;
}

interface Props {
  auth: boolean;
  access: any;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Pinned first, then newest — shared by all mutation handlers
function sortNews(arr: NewsItem[]): NewsItem[] {
  return [...arr].sort((a, b) => {
    if (a.isPinned !== b.isPinned) return b.isPinned ? 1 : -1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

const NewsCard: React.FC<Props> = ({ auth, access }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState<NewsItem | null>(null);

  const isAdmin  = access?.dashboard_settings === true || access?.dashboard_settings === 1;
  const canWrite = true; // любой авторизованный пользователь может публиковать

  const fetchNews = useCallback(async () => {
    try {
      const { data } = await axios.get<NewsItem[]>('news');
      setNews(data);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auth) fetchNews();
  }, [auth, fetchNews]);

  const handleDelete = useCallback(async (item: NewsItem) => {
    const url = isAdmin ? `news/${item.id}/admin` : `news/${item.id}`;
    try {
      await axios.delete(url);
      setNews(prev => prev.filter(n => n.id !== item.id));
      enqueueSnackbar(t('news_delete') || 'Deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar(t('err_save_generic') || 'Error', { variant: 'error' });
    }
  }, [isAdmin, enqueueSnackbar, t]);

  const handlePin = useCallback(async (item: NewsItem) => {
    try {
      const { data } = await axios.patch<NewsItem>(`news/${item.id}/pin`);
      setNews(prev => sortNews(prev.map(n => n.id === data.id ? data : n)));
    } catch {
      enqueueSnackbar(t('err_save_generic') || 'Error', { variant: 'error' });
    }
  }, [enqueueSnackbar, t]);

  const handleAdded = useCallback((item: NewsItem) => {
    setNews(prev => sortNews([item, ...prev]));
  }, []);

  const handleEdited = useCallback((updated: NewsItem) => {
    setNews(prev => sortNews(prev.map(n => n.id === updated.id ? updated : n)));
  }, []);

  const visible = news.slice(0, 3);

  return (
    <>
      <div className="dashboard-card p-[16px]">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#db2777', flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', flex: 1 }}>
            {t('news_announcements') || 'Company announcements'}
          </span>
          {/* All news — пока не делает ничего */}
          <button
            type="button"
            style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'var(--btn-user-form)', color: 'var(--text-color-calendar)', border: '1px solid var(--border-input)', cursor: 'default', opacity: 0.6 }}
          >
            {t('news_all') || 'All news'} →
          </button>
        </div>

        {/* Items */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-input)' }}>
                <div className="skeleton-line" style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0 }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div className="skeleton-line" style={{ width: '60%', height: 14 }} />
                  <div className="skeleton-line" style={{ width: '90%', height: 12 }} />
                </div>
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-color-calendar-week)', fontSize: 12 }}>
            No announcements yet
          </div>
        ) : (
          visible.map((item, idx) => (
            <div
              key={item.id}
              style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: idx < visible.length - 1 ? '1px solid var(--border-input)' : 'none', position: 'relative' }}
              className="group"
            >
              <div style={{ width: 42, height: 42, borderRadius: 10, background: item.emojiColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, transition: 'transform .15s' }}>
                {item.emoji}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)', lineHeight: 1.3 }}>{item.title}</div>
                  {item.isPinned && (
                    <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 9, fontWeight: 800, background: 'rgba(239,68,68,.1)', color: '#dc2626', letterSpacing: '.04em', textTransform: 'uppercase', flexShrink: 0 }}>
                      📌 {t('news_pinned') || 'Pinned'}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-color-calendar)', lineHeight: 1.6 }}>{item.body}</div>
                <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 6 }}>
                  {formatDate(item.createdAt)}
                </div>
              </div>

              {/* Action buttons — visible on hover for authorised users */}
              {canWrite && (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignSelf: 'flex-start' }} className="news-card-actions">
                  {(isAdmin || item.authorId !== undefined) && (
                    <button
                      type="button"
                      title={t('news_edit') || 'Edit'}
                      onClick={() => setEditTarget(item)}
                      style={{ width: 26, height: 26, borderRadius: 7, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color-calendar-week)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, transition: 'all .12s' }}
                    >
                      ✏️
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      title={item.isPinned ? (t('news_unpin') || 'Unpin') : (t('news_pin') || 'Pin')}
                      onClick={() => handlePin(item)}
                      style={{ width: 26, height: 26, borderRadius: 7, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: item.isPinned ? '#dc2626' : 'var(--text-color-calendar-week)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, transition: 'all .12s' }}
                    >
                      📌
                    </button>
                  )}
                  <button
                    type="button"
                    title={t('news_delete') || 'Delete'}
                    onClick={() => handleDelete(item)}
                    style={{ width: 26, height: 26, borderRadius: 7, border: '1px solid rgba(239,68,68,.2)', background: 'rgba(239,68,68,.08)', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, transition: 'all .12s' }}
                  >
                    🗑
                  </button>
                </div>
              )}
            </div>
          ))
        )}

        {/* Add button */}
        {canWrite && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            style={{ width: '100%', marginTop: 14, padding: '9px 16px', borderRadius: 10, border: '1.5px dashed var(--border-input)', background: 'transparent', color: 'var(--text-color-calendar-week)', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all .15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = '#2563eb'; (e.currentTarget as HTMLElement).style.color = '#2563eb'; (e.currentTarget as HTMLElement).style.background = 'rgba(37,99,235,.04)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-input)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-color-calendar-week)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <span style={{ fontSize: 16 }}>＋</span>
            {t('news_add') || 'Add announcement'}
          </button>
        )}

      </div>

      {showAdd && (
        <AddNews
          access={access}
          onClose={() => setShowAdd(false)}
          onSuccess={handleAdded}
        />
      )}

      {editTarget && (
        <EditNews
          news={editTarget}
          access={access}
          onClose={() => setEditTarget(null)}
          onSuccess={handleEdited}
        />
      )}
    </>
  );
};

export default NewsCard;
